import React, { useContext, useState } from "react";
import {
  useStripe,
  useElements,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import {
  attachBankAccount,
  attachPaymentMethod,
} from "../../../services/apiservices/users";
import { useToast } from "../../../hooks/useToast";
import AuthContext from "../../../hooks/authContext";
import { Label } from "../../../components/ui/label";
import { CircularProgress } from "@material-ui/core";
import { CreditCard, Landmark } from "lucide-react";
import { US_STATES } from "../../../components/USStateConstant";

const CardForm = ({ name, setName, setIsAddDialogOpen, fetchCompanyList, messageDialog, setMessageDialog }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const auth = useContext(AuthContext);
  const [loader, setLoader] = useState(false);
  const [paymentMethods, setPaymentMethod] = useState("card");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [achDetails, setAchDetails] = useState({
    routingNumber: "",
    accountNumber: "",
    accountType: "checking",
    accountHolderName: "",
  });

  const isDarkMode = localStorage?.getItem('darkMode') === 'true';

  const handleAddPaymentMethod = async (e) => {
    e.preventDefault();
    setLoader(true);

    if (paymentMethods === "card") {
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        setLoader(false);
        console.error("CardElement not found.");
        return;
      }

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: name || "Card Holder",
        },
      });

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
        setLoader(false);
        return;
      }

      try {
        await attachPaymentMethod(
          paymentMethod.id,
          auth?.userProfile?.customer
        );
        setIsAddDialogOpen(false);
        setLoader(false);
        await fetchCompanyList();
        toast({
          title: "Success",
          description: "Card added successfully",
          variant: "default",
        });
      } catch (err) {
        console.error("Error attaching payment method:", err);
        toast({
          title: "Error",
          description: "Failed to attach card",
          variant: "destructive",
        });
        setLoader(false);
        return;
      }
    } else if (paymentMethods === "ach") {
      try {
       const result = await stripe.createPaymentMethod({
          type: "us_bank_account",
          billing_details: {
            name: achDetails.accountHolderName,
            email: auth?.userProfile?.email,
            address: {
              line1: address,
              city,
              state,
              postal_code: postalCode,
            },
          },
          us_bank_account: {
            routing_number: achDetails.routingNumber,
            account_number: achDetails.accountNumber,
            account_holder_type: "individual",
          },
        });

        if (result.error) {
          toast({
            title: "Error",
            description: result.error.message,
            variant: "destructive",
          });
          setLoader(false);
          return;
        }

        const paymentMethodId = result.paymentMethod.id;

        const attachResult = await attachBankAccount(paymentMethodId, auth?.userProfile?.customer, auth?.userProfile?.id, result.paymentMethod);
        const { clientSecret } = attachResult;

        const confirm = await stripe.confirmUsBankAccountSetup(clientSecret, {
          payment_method: paymentMethodId,
        });

        if (confirm.error) {
          console.log(confirm.error);
        }
        setIsAddDialogOpen(false);

        setTimeout(() => {
          setMessageDialog(true);
        }, 50);
        toast({
          title: 'Success',
          description: 'Bank account added successfully. Please verify the micro-deposits to complete setup.',
          variant: 'default',
        });
      } catch (error) {
        console.error("ACH setup error:", error);
        toast({
          title: "Error",
          description: "Failed to add bank account",
          variant: "destructive",
        });
        setLoader(false);
        return;
      }
    }
  };

  const renderCardFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="cardNumber" className="dark:text-white">Card Number</Label>
        <div className="border rounded px-3 py-2">
          <CardNumberElement
            id="cardNumber"
            options={{
              style: {
                base: {
                  fontSize: "16px",
                   color: isDarkMode ? "#fff" : "#000",
                  "::placeholder": {
                    color: "#a3a3a3",
                  },
                },
              },
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="expiryDate" className="dark:text-white">Expiry Date</Label>
          <div className="border rounded px-3 py-2">
            <CardExpiryElement
              id="expiryDate"
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                     color: isDarkMode ? "#fff" : "#000",
                    "::placeholder": {
                      color: "#a3a3a3",
                    },
                  },
                },
              }}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="cvc" className="dark:text-white">CVC</Label>
          <div className="border rounded px-3 py-2">
            <CardCvcElement
              id="cvc"
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                     color: isDarkMode ? "#fff" : "#000",
                    "::placeholder": {
                      color: "#a3a3a3",
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderACHFields = () => (
    <div className="space-y-2">
      <div className="space-y-2">
        <label className="text-sm font-medium dark:text-white">
          Routing Number
        </label>
        <input
          type="text"
          className="w-full px-3 py-2 border rounded text-sm dark:text-white dark:bg-[#0F1729]"
          placeholder="1234567890"
          value={achDetails.routingNumber}
          onChange={(e) =>
            setAchDetails((prev) => ({
              ...prev,
              routingNumber: e.target.value.replace(/\D/g, ""),
            }))
          }
          required
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium dark:text-white">
          Account Number
        </label>
        <input
          type="text"
          className="w-full px-3 py-2 border rounded text-sm dark:text-white dark:bg-[#0F1729]"
          placeholder="1234567890"
          value={achDetails.accountNumber}
          onChange={(e) =>
            setAchDetails((prev) => ({
              ...prev,
              accountNumber: e.target.value.replace(/\D/g, ""),
            }))
          }
          required
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium dark:text-white">
          Account Type
        </label>
        <select
          className="w-full px-3 py-2 border rounded text-sm bg-white dark:text-white dark:bg-[#0F1729]"
          value={achDetails.accountType}
          onChange={(e) =>
            setAchDetails((prev) => ({
              ...prev,
              accountType: e.target.value,
            }))
          }
          required
        >
          <option value="checking">Checking</option>
          <option value="savings">Savings</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium dark:text-white">
          Account Holder Name
        </label>
        <input
          type="text"
          className="w-full px-3 py-2 border rounded text-sm dark:text-white dark:bg-[#0F1729]"
          placeholder="John Doe"
          value={achDetails.accountHolderName}
          onChange={(e) =>
            setAchDetails((prev) => ({
              ...prev,
              accountHolderName: e.target.value,
            }))
          }
          required
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Address</label>
        <input
          type="text"
          className="w-full px-3 py-2 border rounded text-sm dark:text-white dark:bg-[#0F1729]"
          placeholder="123 Main St"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">City</label>
          <input
            type="text"
            className="w-full px-3 py-2 border rounded text-sm dark:text-white dark:bg-[#0F1729]"
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">State</label>
          <div className="relative">
            <select
              className="w-full px-3 py-2 border bg-white rounded text-sm dark:text-white dark:bg-[#0F1729] appearance-none"
              value={state}
              onChange={(e) => setState(e.target.value)}
              required
            >
              <option value="">Select State</option>
              {US_STATES?.map((s) => (
                <option key={s?.value} value={s?.value}>
                  {s?.label}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-500">
              <svg
                className="h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M5.25 7.75L10 12.5l4.75-4.75" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">ZIP Code</label>
        <input
          type="text"
          className="w-full px-3 py-2 border rounded text-sm dark:text-white dark:bg-[#0F1729]"
          placeholder="ZIP Code"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          required
        />
      </div>
    </div>
  );

  return (
    <>
      <form onSubmit={handleAddPaymentMethod}>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="dark:text-white">Payment Method Type</Label>
            <div className="flex bg-gray-100 rounded p-1">
              <button
                type="button"
                onClick={() => setPaymentMethod("card")}
                className={`flex-1 flex items-center justify-center py-2 px-4 rounded text-sm font-medium transition ${
                  paymentMethods === "card"
                    ? "bg-white shadow text-green-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <CreditCard className="w-5 h-5 mr-8 text-blue-600" /> Card
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod("ach")}
                className={`flex-1 flex items-center justify-center py-2 px-4 rounded text-sm font-medium transition ${
                  paymentMethods === "ach"
                    ? "bg-white shadow text-green-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <Landmark className="w-5 h-5 mr-8 text-green-600" /> Bank
                Account
              </button>
            </div>
          </div>

          {paymentMethods === "card" ? renderCardFields() : renderACHFields()}
          {paymentMethods === "card" && (
            <div className="space-y-2">
              <Label htmlFor="name" className="dark:text-white">
                {paymentMethods === "card"
                  ? "Name on Card"
                  : "Account Holder Name"}
              </Label>
              <Input
                id="name"
                placeholder={
                  paymentMethods === "card" ? "John Doe" : "Account Holder Name"
                }
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setIsAddDialogOpen(false);
              setName("");
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-green-600 hover:bg-green-700 text-white"
            disabled={loader}
          >
            {loader ? (
              <CircularProgress size={20} />
            ) : (
              `Add ${paymentMethods === "card" ? "Card" : "Bank Account"}`
            )}
          </Button>
        </div>
      </form>

      {/* <BankVerificationModal
        open={isVerificationModalOpen}
        onClose={() => setIsVerificationModalOpen(false)}
        paymentMethodId={selectedPaymentMethodId}
      /> */}
    </>
  );
};

export default CardForm;
